import React, { useState, useEffect } from 'react';
import PopupModal from './PopupModal';
import './CalendarSmallBagsProduction.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarXmark } from '@fortawesome/free-solid-svg-icons';
import api from '../api/client';

function CalendarSmallBagsProduction({ title, apiEndpoint, badgeConfig, popupConfig, customDataConfig }) {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    if (isNaN(now.getTime())) {
      console.error('Invalid initial date, defaulting to current date');
      return new Date();
    }
    return now;
  });
  const [productionData, setProductionData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [customData, setCustomData] = useState({});
  const [avgPlanCompletion, setAvgPlanCompletion] = useState(0);

  // Determine calendar type based on title
  const calendarType = title.includes('Small Bags') ? 'small_bags' :
                      title.includes('Fertilizer') ? 'fertilizers' :
                      title.includes('Microgreens') ? 'microgreens' : 'sets';

  // Fetch production data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isNaN(currentDate.getTime())) {
          console.error('Invalid currentDate in fetchData:', currentDate);
          return;
        }
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        if (!year || !month || isNaN(year) || isNaN(month)) {
          console.error(`Invalid year or month in fetchData: year=${year}, month=${month}`);
          return;
        }
        const url = `${apiEndpoint}?year=${year}&month=${month}`;
        console.log('Fetching production data from:', url);
        const response = await api.get(url);
        const data = response.data;
        console.log('Received production data:', data);
        setProductionData(data);

        // Calculate average plan completion percentage for the month, excluding holidays, weekends, and incomplete days (current and future)
        if (calendarType !== 'small_bags') {
          let totalProduced = 0;
          let totalPlanned = 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize to compare dates only
          const eligibleDays = [];
          Object.keys(data).forEach(dateKey => {
            const [y, m, d] = dateKey.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            date.setHours(0, 0, 0, 0);
            const isHoliday = holidays.includes(dateKey);
            const dayOfWeek = date.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
            const isIncomplete = date.getTime() >= today.getTime();
            if (!isHoliday && !isWeekend && !isIncomplete) {
              const dayData = data[dateKey];
              const produced = Number(dayData.produced) || 0;
              const planned = Number(dayData.planned) || 0;
              totalProduced += produced;
              totalPlanned += planned;
              eligibleDays.push({ dateKey, produced, planned });
            }
          });
          console.log('Eligible days for avg plan completion:', eligibleDays);
          let avgCompletion = 0;
          if (totalPlanned === 0) {
            avgCompletion = totalProduced === 0 ? 100 : 0;
          } else {
            avgCompletion = Math.round((totalProduced / totalPlanned) * 100);
          }
          console.log(`Avg Plan Completion: ${avgCompletion}% (Total Produced: ${totalProduced}, Total Planned: ${totalPlanned})`);
          setAvgPlanCompletion(avgCompletion);
        }
      } catch (error) {
        console.error('Error fetching production data:', error.message);
      }
    };
    fetchData();
  }, [currentDate, apiEndpoint, holidays, calendarType]);

  // Fetch holidays
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        if (isNaN(currentDate.getTime())) {
          console.error('Invalid currentDate in fetchHolidays:', currentDate);
          return;
        }
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        if (!year || !month || isNaN(year) || isNaN(month)) {
          console.error(`Invalid year or month in fetchHolidays: year=${year}, month=${month}`);
          return;
        }
        const url = `/api/holidays?year=${year}&month=${month}&calendar_type=${calendarType}`;
        console.log('Fetching holidays from:', url);
        const response = await api.get(url);
        const data = response.data;
        console.log('Received holidays:', data);
        // Ensure holiday_date is in YYYY-MM-DD format
        const formattedHolidays = data.map(h => {
          const date = new Date(h.holiday_date);
          date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
          return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }).filter(Boolean);
        setHolidays(formattedHolidays);
      } catch (error) {
        console.error('Error fetching holidays:', error.message);
      }
    };
    fetchHolidays();
  }, [currentDate, calendarType]);

  // Fetch custom data
  useEffect(() => {
    const fetchCustomData = async () => {
      if (!customDataConfig || !Array.isArray(customDataConfig)) {
        console.error('Invalid or missing customDataConfig:', customDataConfig);
        setCustomData({});
        return;
      }
      if (isNaN(currentDate.getTime())) {
        console.error('Invalid currentDate in fetchCustomData:', currentDate);
        const fallbackData = {};
        customDataConfig.forEach((config) => {
          fallbackData[config.label] = config.label.includes('Production') 
            ? { daysOfProduction: 'N/A', daysOfSupply: 'N/A' } 
            : {};
        });
        setCustomData(fallbackData);
        return;
      }
      
      const newCustomData = {};
      try {
        for (const config of customDataConfig) {
          if (!config || !config.endpoint || !config.label || !config.fields) {
            console.warn(`Invalid config object:`, config);
            newCustomData[config.label] = config.label?.includes('Production') 
              ? { daysOfProduction: 'N/A', daysOfSupply: 'N/A' } 
              : {};
            continue;
          }
          
          const year = currentDate.getFullYear();
          const month = currentDate.getMonth() + 1;
          console.log(`Preparing to fetch custom data for ${config.label}: year=${year}, month=${month}`);
          
          if (!year || !month || isNaN(year) || isNaN(month)) {
            console.error(`Invalid year or month for ${config.label}: year=${year}, month=${month}`);
            newCustomData[config.label] = config.label.includes('Production') 
              ? { daysOfProduction: 'N/A', daysOfSupply: 'N/A' } 
              : {};
            continue;
          }

          // Determine if date parameters are needed
          const requiresDateParams = config.label.includes('Production');
          const url = requiresDateParams
            ? `${config.endpoint}?year=${year}&month=${month}`
            : config.endpoint;
            
          console.log('Fetching custom data from:', url);
          const response = await api.get(url);

          const data = response.data;
          console.log(`Received custom data for ${config.label}:`, data);
          
          // Validate data format
          if (config.label.includes('Production') && !data.hasOwnProperty('daysOfProduction')) {
            console.warn(`Invalid data format for ${config.label}:`, data);
            newCustomData[config.label] = { daysOfProduction: 'N/A', daysOfSupply: 'N/A' };
          } else {
            // Calculate Days of Supply for Average Daily Production
            if (config.label === 'Average Daily Production') {
              const emptyBagsConfig = customDataConfig.find(c => c.label === 'Small Bags without Seeds');
              if (emptyBagsConfig) {
                const emptyBagsUrl = `${emptyBagsConfig.endpoint}?year=${year}&month=${month}`;
                console.log('Fetching empty bags data from:', emptyBagsUrl);
                const emptyBagsResponse = await api.get(emptyBagsUrl);
                const emptyBagsData = emptyBagsResponse.data;
                console.log('Received empty bags data:', emptyBagsData);
                const totalEmptyBags = (Number(emptyBagsData['Organo Republic']) || 0) + (Number(emptyBagsData['Seedphony']) || 0);
                const avgDailyProduction = Number(data.daysOfProduction) || 0;
                const daysOfSupply = avgDailyProduction > 0 ? (totalEmptyBags / avgDailyProduction).toFixed(0) : 'N/A';
                newCustomData[config.label] = {
                  ...data,
                  daysOfSupply: daysOfSupply
                };
              } else {
                newCustomData[config.label] = {
                  ...data,
                  daysOfSupply: 'N/A'
                };
              }
            } else {
              newCustomData[config.label] = data;
            }
          }
        }
        setCustomData(newCustomData);
      } catch (error) {
        console.error('Error fetching custom data:', error.message);
        // Set default data on error
        customDataConfig.forEach((config) => {
          if (!newCustomData[config.label]) {
            newCustomData[config.label] = config.label.includes('Production') 
              ? { daysOfProduction: 'N/A', daysOfSupply: 'N/A' } 
              : {};
          }
        });
        setCustomData(newCustomData);
      }
    };
    
    fetchCustomData();
  }, [currentDate, customDataConfig]);

  // Toggle holiday status
  const toggleHoliday = async (day) => {
    const dateKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    console.log(`Toggling holiday for ${dateKey}, calendar_type: ${calendarType}`);

    try {
      if (holidays.includes(dateKey)) {
        // Remove holiday
        console.log('Sending DELETE request to remove holiday:', dateKey);
        await api.delete('/api/holidays', {
          data: { holiday_date: dateKey, calendar_type: calendarType },
        }).catch(err => {
          if (!err.response) throw err;
          throw new Error(err.response.data?.error || `Failed to remove holiday (Status: ${err.response.status})`);
        });
        console.log('Holiday removed successfully:', dateKey);
        setHolidays(holidays.filter(h => h !== dateKey));
        // Refresh holidays to ensure UI is in sync
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        const refreshResponse = await api.get(`/api/holidays?year=${year}&month=${month}&calendar_type=${calendarType}`).catch(err => { if (!err.response) throw err; return null; });
        if (refreshResponse) {
          const data = refreshResponse.data;
          setHolidays(data.map(h => {
            const date = new Date(h.holiday_date);
            return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
          }));
        }
      } else {
        // Add holiday
        console.log('Sending POST request to add holiday:', dateKey);
        await api.post('/api/holidays', {
          holiday_date: dateKey, calendar_type: calendarType,
        }).catch(err => {
          if (!err.response) throw err;
          throw new Error(err.response.data?.error || `Failed to add holiday (Status: ${err.response.status})`);
        });
        console.log('Holiday added successfully:', dateKey);
        setHolidays([...holidays, dateKey]);
        // Refresh holidays to ensure UI is in sync
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        const refreshResponse = await api.get(`/api/holidays?year=${year}&month=${month}&calendar_type=${calendarType}`).catch(err => { if (!err.response) throw err; return null; });
        if (refreshResponse) {
          const data = refreshResponse.data;
          setHolidays(data.map(h => {
            const date = new Date(h.holiday_date);
            return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
          }));
        }
      }
    } catch (error) {
      console.error('Error toggling holiday:', error.message);
    }
  };

  // Handle date click
  const handleDateClick = (day) => {
    if (popupConfig?.enabled) {
      const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      setSelectedDate(clickedDate);
      setIsPopupOpen(true);
    }
  };

  // Labor cost per bag: дневной фонд оплаты (laborCost) теперь приходит
  // с сервера в summary_calendar — ставки сотрудников не должны лежать
  // в клиентском бандле, его видит любой залогиненный.
  const calculateLaborCostPerBag = (produced, totalLost, laborCost) => {
    if (!(produced > 0)) return '0.00';
    return ((Number(laborCost) || 0) / produced + totalLost / produced).toFixed(4);
  };

  // Generate array of days
  const days = [];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const startingDay = firstDayOfMonth.getDay();

  // Adjust starting day to account for Sunday being hidden (0 becomes 6, 1 becomes 0, etc.)
  const adjustedStartingDay = startingDay === 0 ? 6 : startingDay - 1;

  // Add empty cells for days before the first of the month, adjusted for 6-day week
  for (let i = 0; i < adjustedStartingDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  // Get current date for highlighting
  const now = new Date();

  // Generate days, skipping Sundays
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
    const isSunday = date.getDay() === 0;
    if (isSunday) {
      continue; // Skip Sundays
    }

    const dateKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
    const dayData = productionData[dateKey] || {};

    // Check if the day is a holiday or Saturday
    const isSaturday = date.getDay() === 6;
    const isHoliday = holidays.includes(dateKey);
    const isNonWorkingDay =  isHoliday;

    // Check if it's today
    const isToday = i === now.getDate() && currentDate.getMonth() === now.getMonth() && currentDate.getFullYear() === now.getFullYear();

    // Calculate plan completion percentage (only for non-Small Bags calendars)
    let planCompletion = 0;
    let planCompletionClass = '';
    let planCompletionColor = '';
    if (calendarType !== 'small_bags') {
      const produced = Number(dayData.produced) || 0;
      const planned = Number(dayData.planned) || 0;
      if (planned === 0) {
        planCompletion = produced === 0 ? 100 : 0;
      } else {
        planCompletion = Math.round((produced / planned) * 100);
      }
      planCompletionClass = planCompletion >= 100 ? 'plan-completion-green' :
                           planCompletion >= 51 ? 'plan-completion-yellow' : 'plan-completion-red';
      planCompletionColor = planCompletion >= 100 ? '#2e7d32' : planCompletion >= 51 ? '#e67a00' : '#d32f2f';
    }

    days.push(
      <div
        key={i}
        className={`calendar-day ${popupConfig?.enabled && !isNonWorkingDay ? 'clickable' : ''} ${isHoliday ? 'holiday' : ''} ${isToday ? 'current-day' : ''}`}
        onClick={() => !isNonWorkingDay && handleDateClick(i)}
      >
        <span className="day-number">{i}</span>
        <span
        className={`holiday-toggle ${isHoliday ? 'holiday-active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleHoliday(i);
        }}
        title={isHoliday ? 'Remove holiday' : 'Mark as holiday'}
      >
        <FontAwesomeIcon icon={faCalendarXmark} />
      </span>
        {!isNonWorkingDay && (
          <div className="badges">
            {badgeConfig.map((badge, index) => {
              // Skip planCompletion for Small Bags calendar
              if (calendarType === 'small_bags' && badge.key === 'planCompletion') {
                return null;
              }
              // Skip inProduction if value is 0 or undefined for Small Bags calendar
              if (calendarType === 'small_bags' && badge.key === 'inProduction') {
                const value = Number(dayData[badge.key]) || 0;
                if (value <= 0) {
                  return null;
                }
              }
              const value = Number(dayData[badge.key]) || 0;
              // Seeds off plan: shown only on days that have such operations
              if (badge.key === 'seedsOff' && value <= 0) {
                return null;
              }
              const isUnderProduced = badge.key === 'produced' && value < (dayData.planned || 0);
              if (badge.key === 'whoDidntMeetPlan' && planCompletion >= 100) {
                return null;
              }
              // Custom tooltip for totalLost
              const tooltipText = badge.key === 'totalLost' 
                ? `Total Lost\nSeeds: $${Number(dayData.seedsLost) || 0}\nBags: $${Number(dayData.bagsLost) || 0}`
                : badge.key === 'seedsOff'
                ? `Operations with seeds off plan by more than 5%: ${value}\nUnderused that day: ${Number(dayData.seedsUnder) || 0} lb\nOverused that day: ${Number(dayData.seedsOver) || 0} lb\nOpen the day to see which operations.`
                : badge.tooltip;
              return (
                <div key={index} className={`badge ${badge.key.toLowerCase().replace(/\s/g, '-')} ${badge.key === 'planCompletion' ? planCompletionClass : ''}`} title={tooltipText}>
                  {badge.icon && badge.key === 'planCompletion' ? (
                    <span className="badge-icon">
                      <FontAwesomeIcon icon={badge.icon.props.icon} style={{ fontSize: '18px', color: planCompletionColor }} />
                    </span>
                  ) : (
                    badge.icon && <span className="badge-icon">{badge.icon}</span>
                  )}
                  {badge.label}
                  {badge.key === 'produced' ? (
                    <span>
                      {value} / <span className={isUnderProduced ? 'under-produced' : ''}>{dayData.planned || 0}</span>
                    </span>
                  ) : badge.key === 'planCompletion' ? (
                    <span>{planCompletion}%</span>
                  ) : badge.key === 'laborCostPerBag' ? (
                    <span>${calculateLaborCostPerBag(Number(dayData.produced) || 0, Number(dayData.totalLost) || 0, dayData.laborCost)}</span>
                  ) : badge.key === 'whoDidntMeetPlan' ? (
                    <div className="names-list">
                      {(dayData[badge.key] ? 
                        dayData[badge.key].split(', ').map(name => name.trim()) : 
                        ['None']
                      ).map((name, idx) => (
                        <span key={idx} className="name">{name}</span>
                      ))}
                    </div>
                  ) : (
                    value
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Month and year selection
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    } else {
      console.error('Invalid date in handlePrevMonth:', newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    } else {
      console.error('Invalid date in handleNextMonth:', newDate);
    }
  };

  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value);
    const newDate = new Date(currentDate.getFullYear(), newMonth, 1);
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    } else {
      console.error('Invalid date in handleMonthChange:', newDate);
    }
  };

  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    const newDate = new Date(newYear, currentDate.getMonth(), 1);
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    } else {
      console.error('Invalid date in handleYearChange:', newDate);
    }
  };

  // Generate years for dropdown
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }

  // Determine color for average plan completion (only for non-Small Bags calendars)
  const avgPlanCompletionClass = calendarType !== 'small_bags' ? (
    avgPlanCompletion >= 100 ? 'plan-completion-green' :
    avgPlanCompletion >= 51 ? 'plan-completion-yellow' : 'plan-completion-red'
  ) : '';
  const avgPlanCompletionColor = calendarType !== 'small_bags' ? (
    avgPlanCompletion >= 100 ? '#2e7d32' : avgPlanCompletion >= 51 ? '#e67a00' : '#d32f2f'
  ) : '';

  return (
    <div className="small-bags-production">
      <h2>{title}</h2>
      <div className="custom-data-section">
        {customDataConfig?.map((config, index) => (
          <div key={index} className="custom-data-item">
            <span className="custom-data-label">{config.label}:</span>
            <span className="custom-data-value">
              {customData[config.label] ? (
                config.label === 'Average Daily Production' ? (
                  <span>
                    {config.fields.map((field, fieldIndex) => (
                      <span key={fieldIndex}>
                        {field.label} - {customData[config.label][field.key] !== undefined ? customData[config.label][field.key] : '0'}
                        {fieldIndex < config.fields.length - 1 ? ' / ' : ''}
                      </span>
                    ))}
                  </span>
                ) : (
                  config.fields.map((field, fieldIndex) => (
                    <span key={fieldIndex}>
                      {field.label} - {customData[config.label][field.key] !== undefined ? customData[config.label][field.key] : '0'}
                      {fieldIndex < config.fields.length - 1 && config.label.includes('Bags') && !config.label.includes('Production') ? ', ' : ''}
                    </span>
                  ))
                )
              ) : (
                'Loading...'
              )}
            </span>
          </div>
        ))}
      </div>
      <div className="calendar-controls-container">
        {calendarType !== 'small_bags' && (
          <div className={`avg-plan-completion ${avgPlanCompletionClass}`}>
            <span className="avg-plan-completion-icon">
              <FontAwesomeIcon icon={badgeConfig.find(b => b.key === 'planCompletion')?.icon.props.icon} style={{ fontSize: '16px', color: avgPlanCompletionColor }} />
            </span>
            Avg Plan: {avgPlanCompletion}%
          </div>
        )}
        <div className="calendar-controls">
          <button onClick={handlePrevMonth}>&lt;</button>
          <select value={currentDate.getMonth()} onChange={handleMonthChange}>
            {months.map((month, index) => (
              <option key={month} value={index}>{month}</option>
            ))}
          </select>
          <select value={currentDate.getFullYear()} onChange={handleYearChange}>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button onClick={handleNextMonth}>&gt;</button>
        </div>
      </div>
      <div className="calendar">
        <div className="calendar-header">
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
        </div>
        <div className="calendar-grid">
          {days}
        </div>
      </div>
      {popupConfig?.enabled && (
        <PopupModal
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          date={selectedDate}
          endpoint={popupConfig.endpoint}
          columns={popupConfig.columns}
          hint={popupConfig.hint}
        />
      )}
    </div>
  );
}

export default CalendarSmallBagsProduction;